import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Heart, Clock, Calendar } from "lucide-react";
import api from "../lib/api";

export const ARTICLES = [
  {
    slug: "rencontre-serieuse-diaspora-africaine",
    title: "Rencontre sérieuse dans la diaspora africaine : 7 conseils pour trouver l'amour loin du pays",
    description:
      "Vous vivez en Europe, aux USA ou au Canada et cherchez une rencontre sérieuse avec un Africain ou une Africaine ? Découvrez 7 conseils concrets pour trouver l'amour dans la diaspora.",
    image: "/blog/couple-diaspora.jpg",
    imageAlt: "Couple africain heureux à la terrasse d'un café en Europe",
    date: "2026-07-28",
    dateFr: "28 juillet 2026",
    readTime: "6 min",
    tag: "Conseils rencontre",
    sections: [
      {
        h2: null,
        paras: [
          "Vivre en Europe, aux États-Unis, au Canada ou en Chine tout en gardant ses racines africaines, c'est une richesse… mais aussi un vrai défi quand on cherche l'amour. Comment rencontrer une personne qui partage votre culture, vos valeurs et votre vision de la vie quand on est à des milliers de kilomètres du pays ? Voici 7 conseils concrets pour réussir votre rencontre sérieuse dans la diaspora africaine.",
        ],
      },
      {
        h2: "1. Clarifiez votre intention dès le départ",
        paras: [
          "Amitié, relation sérieuse ou mariage ? Sur un site de rencontre africain, préciser son intention dès l'inscription évite les malentendus et fait gagner un temps précieux. Une personne qui cherche une rencontre en vue du mariage n'a pas les mêmes attentes qu'une personne qui souhaite d'abord élargir son cercle amical. C'est d'ailleurs pour cela que KoraLove demande à chaque membre de choisir son but : rencontre amicale ou rencontre pour mariage.",
        ],
      },
      {
        h2: "2. Privilégiez les profils vérifiés",
        paras: [
          "Le premier frein aux rencontres en ligne, ce sont les faux profils. Choisissez une plateforme qui vérifie l'identité de ses membres, par exemple avec une vérification par selfie. Vous discutez ainsi avec de vraies personnes, réellement à la recherche d'une relation.",
        ],
      },
      {
        h2: "3. Filtrez par pays d'origine et de résidence",
        paras: [
          "Rencontrer quelqu'un du même pays d'origine — Cameroun, Sénégal, Côte d'Ivoire, Congo, Mali… — facilite naturellement la complicité : mêmes plats, mêmes musiques, mêmes souvenirs. Et filtrer par pays de résidence vous permet de trouver une personne proche de chez vous, en France, en Belgique, aux USA ou au Canada, pour pouvoir vous rencontrer réellement.",
        ],
      },
      {
        h2: "4. Soignez votre profil (photos et bio)",
        paras: [
          "Une photo récente, souriante et naturelle vaut mieux que dix photos filtrées. Dans votre bio, parlez de vos valeurs, de votre rapport à la famille, de vos passions. Les profils complets reçoivent beaucoup plus de messages : c'est votre vitrine, prenez 10 minutes pour la soigner.",
        ],
      },
      {
        h2: "5. Posez les bonnes questions dès les premiers échanges",
        paras: [
          "La religion, le projet de retour au pays, la place de la famille, le désir d'enfants… Dans la diaspora, ces sujets comptent énormément. N'ayez pas peur de les aborder tôt, avec respect. Une conversation sincère dès le début pose les bases d'une relation solide.",
        ],
      },
      {
        h2: "6. Prenez votre temps, mais soyez constant",
        paras: [
          "Une rencontre sérieuse ne se construit pas en trois messages. Répondez régulièrement, proposez un appel vidéo quand la confiance est installée : c'est le meilleur moyen de valider le feeling avant une rencontre réelle, surtout quand plusieurs villes ou pays vous séparent.",
        ],
      },
      {
        h2: "7. Restez prudent et protégez-vous",
        paras: [
          "N'envoyez jamais d'argent à quelqu'un que vous n'avez pas rencontré, ne partagez pas trop vite vos informations personnelles, et signalez tout comportement suspect. Les plateformes sérieuses offrent des outils de signalement et de blocage : utilisez-les sans hésiter.",
          "Trouver l'amour dans la diaspora africaine, c'est possible — des milliers de couples se sont formés en ligne. Avec une intention claire, un profil soigné et une plateforme pensée pour les Africains de la diaspora, vous mettez toutes les chances de votre côté.",
        ],
      },
    ],
  },
  {
    slug: "rencontre-mariage-africain-diaspora",
    title: "Rencontre en vue du mariage : comment concilier traditions africaines et vie dans la diaspora",
    description:
      "Trouver un mari ou une femme qui respecte les traditions africaines quand on vit en Europe ou en Amérique : dot, familles, religion… nos conseils pour une rencontre en vue du mariage réussie.",
    image: "/blog/mariage-africain.jpg",
    imageAlt: "Mariage traditionnel africain en tenues colorées",
    date: "2026-07-29",
    dateFr: "29 juillet 2026",
    readTime: "7 min",
    tag: "Mariage",
    sections: [
      {
        h2: null,
        paras: [
          "Pour beaucoup d'Africains et d'Africaines de la diaspora, le mariage n'est pas qu'une histoire à deux : c'est l'union de deux familles, le respect de traditions transmises depuis des générations, et souvent un lien fort avec le pays d'origine. Alors comment trouver la bonne personne quand on vit à Paris, Bruxelles, Montréal ou New York ? Voici nos conseils.",
        ],
      },
      {
        h2: "Assumez votre projet de mariage",
        paras: [
          "Beaucoup hésitent à dire clairement qu'ils cherchent un mari ou une femme, de peur de « faire fuir ». C'est une erreur : les personnes qui partagent ce projet seront au contraire rassurées par votre sérieux. Sur KoraLove, le but « rencontre pour mariage » est affiché sur votre profil : vous ne rencontrez que des personnes alignées avec votre projet de vie.",
        ],
      },
      {
        h2: "Parlez des traditions dès les premières conversations",
        paras: [
          "La dot, la rencontre des familles, le mariage coutumier, religieux ou civil… chaque pays, chaque ethnie, chaque famille a ses codes. Aborder ces sujets tôt permet de vérifier que vous partagez la même vision. Quelques questions simples : quelle place ta famille aura-t-elle dans la décision ? Envisages-tu un mariage coutumier au pays ? Quelle importance accordes-tu à la religion ?",
        ],
      },
      {
        h2: "Impliquez les familles au bon moment",
        paras: [
          "Dans la culture africaine, l'avis des parents et des aînés compte. Mais dans la diaspora, les familles sont souvent loin. Les appels vidéo sont vos alliés : présenter votre partenaire à votre mère restée à Douala ou à Dakar par vidéo est devenu tout à fait normal. Prenez ce temps : une famille impliquée tôt est une famille qui soutient votre union.",
        ],
      },
      {
        h2: "Distance et retour au pays : parlez-en franchement",
        paras: [
          "Vous vivez au Canada, l'autre en France ? L'un de vous rêve de retourner au pays dans 5 ans ? Ces questions doivent être posées avant l'engagement, pas après. Un couple solide se construit sur un projet de vie commun : lieu de vie, carrière, éducation des enfants, allers-retours au pays.",
        ],
      },
      {
        h2: "Prenez le temps de vérifier la sincérité",
        paras: [
          "Le mariage attire malheureusement aussi des personnes mal intentionnées (mariage gris, intérêt pour les papiers ou l'argent). Protégez-vous : privilégiez les profils vérifiés par selfie, faites des appels vidéo réguliers, rencontrez la personne en vrai avant tout engagement, et écoutez votre entourage.",
          "Un mariage heureux dans la diaspora, c'est l'alliance du cœur, des familles et des cultures. En choisissant une plateforme dédiée aux rencontres sérieuses entre Africains, vous partez déjà avec les bonnes fondations.",
        ],
      },
    ],
  },
  {
    slug: "amitie-diaspora-africaine-se-faire-des-amis",
    title: "Se faire des amis africains en Europe, aux USA ou au Canada : le guide de l'amitié dans la diaspora",
    description:
      "Nouvellement arrivé en France, en Belgique, aux USA ou au Canada ? Découvrez comment rencontrer des amis africains près de chez vous et recréer une communauté chaleureuse loin du pays.",
    image: "/blog/amitie-africaine.jpg",
    imageAlt: "Groupe d'amis africains riant ensemble dans un parc",
    date: "2026-07-30",
    dateFr: "30 juillet 2026",
    readTime: "5 min",
    tag: "Amitié",
    sections: [
      {
        h2: null,
        paras: [
          "S'installer dans un nouveau pays, c'est excitant… et parfois très solitaire. Le mal du pays, les plats qu'on ne retrouve pas, les blagues que personne ne comprend : tous les membres de la diaspora africaine sont passés par là. La bonne nouvelle ? Il n'a jamais été aussi simple de rencontrer des amis africains près de chez soi.",
        ],
      },
      {
        h2: "Pourquoi l'amitié dans la diaspora est si précieuse",
        paras: [
          "Un ami qui comprend votre culture, c'est quelqu'un avec qui vous pouvez cuisiner un ndolé ou un thiéboudienne, regarder la CAN en criant, parler du pays sans devoir tout expliquer. Ces amitiés sont un ancrage : elles réduisent l'isolement, facilitent l'intégration (bons plans logement, travail, démarches) et font un bien fou au moral.",
        ],
      },
      {
        h2: "Les sites de rencontre amicale : une solution moderne",
        paras: [
          "On pense souvent que les applications de rencontre ne servent qu'à trouver l'amour. C'est faux : de plus en plus de plateformes, comme KoraLove, proposent un mode « rencontre amicale » où vous précisez que vous cherchez des amis, pas une relation. Vous filtrez par pays d'origine et ville de résidence, et vous discutez avec des personnes qui, comme vous, veulent élargir leur cercle.",
        ],
      },
      {
        h2: "Nos conseils pour transformer un match en vraie amitié",
        paras: [
          "Proposez rapidement une activité concrète : un restaurant africain du quartier, un match de foot, une église ou une mosquée, un événement culturel. L'amitié se construit dans la vraie vie. Restez régulier : un message par semaine entretient le lien. Et présentez vos nouveaux amis entre eux — c'est comme ça que naissent les belles communautés.",
        ],
      },
      {
        h2: "Amitié d'abord, et qui sait…",
        paras: [
          "Beaucoup de belles histoires commencent par une amitié sincère. En rencontrant des personnes qui partagent vos valeurs et votre culture, vous construisez un réseau qui peut, un jour, vous mener vers bien plus. Mais chaque chose en son temps : commencez par dire bonjour.",
          "Rejoignez la communauté KoraLove : des milliers d'Africains et d'Africaines en Europe, aux USA, en Chine et au Canada vous attendent pour des rencontres amicales ou sérieuses, en toute simplicité.",
        ],
      },
    ],
  },
];

const setMeta = (title, description, canonicalPath) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", description);
  let l = document.querySelector('link[rel="canonical"]');
  if (!l) {
    l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    document.head.appendChild(l);
  }
  l.setAttribute("href", `https://koralove.org${canonicalPath}`);
};

const BlogHeader = () => {
  const navigate = useNavigate();
  return (
    <header className="bg-kora-ink text-white">
      <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
        <button data-testid="blog-back" onClick={() => navigate("/")} className="text-white/90 hover:text-white">
          <ArrowLeft size={22} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-kora-terracotta flex items-center justify-center">
            <Heart size={14} className="text-white" fill="white" />
          </div>
          <span className="font-display font-bold text-lg">KoraLove</span>
        </div>
      </div>
    </header>
  );
};

const useArticles = () => {
  const [apiArticles, setApiArticles] = useState([]);
  useEffect(() => {
    api.get("/blog").then(({ data }) => setApiArticles(data.articles || [])).catch(() => {});
  }, []);
  return useMemo(() => {
    const slugs = new Set(apiArticles.map((a) => a.slug));
    return [...apiArticles, ...ARTICLES.filter((a) => !slugs.has(a.slug))]
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [apiArticles]);
};

export const Blog = () => {
  const articles = useArticles();
  useEffect(() => {
    setMeta(
      "Blog KoraLove — Conseils rencontre, mariage et amitié pour la diaspora africaine",
      "Conseils pour réussir vos rencontres amoureuses, votre projet de mariage ou vous faire des amis dans la diaspora africaine en Europe, aux USA et au Canada.",
      "/blog"
    );
  }, []);

  return (
    <div className="min-h-screen bg-kora-sand" data-testid="blog-page">
      <BlogHeader />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="inline-flex items-center gap-2 text-kora-terracotta font-bold text-sm mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-kora-terracotta" /> Le blog KoraLove
        </p>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-kora-ink mb-2">Conseils rencontre, mariage & amitié</h1>
        <p className="text-kora-ink/60 mb-8">Nos guides pour la diaspora africaine en Europe, aux USA, en Chine et au Canada.</p>

        <div className="space-y-6">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`blog-card-${a.slug}`}
              className="block bg-white rounded-3xl border border-kora-line overflow-hidden hover:border-kora-terracotta/60 hover:shadow-lg transition-all group"
            >
              <div className="md:flex">
                <div className="md:w-64 shrink-0 aspect-[16/9] md:aspect-auto overflow-hidden">
                  <img src={a.image} alt={a.imageAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 md:p-6">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-kora-terracotta bg-kora-terracotta/10 rounded-full px-2.5 py-1">{a.tag}</span>
                  <h2 className="font-display font-black text-lg text-kora-ink mt-3 leading-snug group-hover:text-kora-terracotta transition-colors">{a.title}</h2>
                  <p className="text-sm text-kora-ink/60 mt-2 line-clamp-2">{a.description}</p>
                  <p className="flex items-center gap-3 text-xs text-kora-muted mt-3">
                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> {a.dateFr}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.readTime} de lecture</span>
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const articles = useArticles();
  const local = ARTICLES.find((a) => a.slug === slug);
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!local);
  useEffect(() => {
    if (local) return;
    setLoading(true);
    setFetched(null);
    api.get(`/blog/${slug}`)
      .then(({ data }) => setFetched(data))
      .catch(() => setFetched(null))
      .finally(() => setLoading(false));
  }, [slug, local]);
  const article = local || fetched;

  useEffect(() => {
    if (!article) return;
    setMeta(`${article.title} | Blog KoraLove`, article.description, `/blog/${article.slug}`);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      image: article.image?.startsWith("http") ? article.image : `https://koralove.org${article.image}`,
      datePublished: article.date,
      author: { "@type": "Organization", name: "KoraLove" },
      publisher: { "@type": "Organization", name: "KoraLove", url: "https://koralove.org" },
    });
    document.head.appendChild(ld);
    window.scrollTo(0, 0);
    return () => document.getElementById("blog-jsonld")?.remove();
  }, [article]);

  if (!article) {
    if (loading) {
      return (
        <div className="min-h-screen bg-kora-sand" data-testid="blog-article-loading">
          <BlogHeader />
          <div className="py-24 flex justify-center">
            <div className="w-10 h-10 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-kora-sand" data-testid="blog-notfound">
        <BlogHeader />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="text-kora-muted mb-4">Cet article n'existe pas.</p>
          <Link to="/blog" className="font-bold text-kora-terracotta">← Voir tous les articles</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kora-sand" data-testid="blog-article-page">
      <BlogHeader />
      <article className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/blog" data-testid="blog-back-to-list" className="text-sm font-bold text-kora-terracotta hover:text-kora-ochre transition-colors">← Tous les articles</Link>
        <span className="block mt-4">
          <span className="text-[11px] font-bold uppercase tracking-wide text-kora-terracotta bg-kora-terracotta/10 rounded-full px-2.5 py-1">{article.tag}</span>
        </span>
        <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-kora-ink mt-3 leading-tight">{article.title}</h1>
        <p className="flex items-center gap-3 text-xs text-kora-muted mt-3 mb-6">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {article.dateFr}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {article.readTime} de lecture</span>
        </p>
        <div className="rounded-3xl overflow-hidden mb-8 aspect-[16/9]">
          <img src={article.image} alt={article.imageAlt} className="w-full h-full object-cover" />
        </div>

        <div className="space-y-4 text-kora-ink/80 leading-relaxed">
          {article.sections.map((s, i) => (
            <React.Fragment key={i}>
              {s.h2 && <h2 className="font-display font-bold text-xl text-kora-ink mt-8 mb-2">{s.h2}</h2>}
              {s.paras.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-3xl border border-kora-line p-6 text-center">
          <h3 className="font-display font-black text-lg text-kora-ink">Prêt(e) à faire de belles rencontres ?</h3>
          <p className="text-sm text-kora-muted mt-1 mb-4">Rejoignez KoraLove gratuitement — amitié ou mariage, à vous de choisir.</p>
          <button
            data-testid="blog-cta-signup"
            onClick={() => navigate("/")}
            className="bg-kora-terracotta text-white font-bold rounded-full px-8 py-3 hover:opacity-90 transition-opacity"
          >
            Créer mon profil gratuit
          </button>
        </div>

        <div className="mt-10">
          <p className="font-display font-bold text-sm uppercase tracking-wider text-kora-muted mb-3">À lire aussi</p>
          <div className="space-y-2">
            {articles.filter((a) => a.slug !== article.slug).slice(0, 4).map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`blog-related-${a.slug}`} className="block bg-white rounded-2xl border border-kora-line p-4 hover:border-kora-terracotta/60 transition-colors">
                <p className="font-bold text-sm text-kora-ink">{a.title}</p>
                <p className="text-xs text-kora-muted mt-1">{a.readTime} de lecture</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
};
